import { HTMLAttributes } from 'react'
import { default as NextLink } from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Link from '@/components/base/link'
import Media, { MediaElement } from '@/components/base/media'
import MediaParallax from '@/components/base/media-parallax'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { asymmetricPushRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type AsymmetricPushProps = HTMLAttributes<HTMLElement> &
  Partial<{
    overline: string
    title: string
    paragraph: string
    link: WPLink
    media_1_media: MediaElement
    media_2_media: MediaElement
    media_3_media: MediaElement
  }>

/**
 * Asymmetric push A
 */
const AsymmetricPushA = ({
  overline,
  title,
  paragraph,
  link,
  media_1_media: media_1,
  media_2_media: media_2,
  ...props
}: AsymmetricPushProps) => {
  return (
    <Motion animation={asymmetricPushRevealAnimation}>
      {(motionRef) => (
        <Container ref={motionRef} {...props}>
          <Grid className="items-end gap-y-gutter">
            <div
              className={cn(
                'col-span-4 md:col-span-3 lg:col-span-4',
                'grid gap-y-[3.2rem] md:gap-y-[6.4rem]'
              )}
            >
              <div className="grid gap-y-[0.8rem]">
                {overline ? (
                  <Typography
                    variant="label"
                    component="h2"
                    className="font-gillsans uppercase text-copper"
                    data-motion-overline
                  >
                    {overline}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography
                    variant="h4"
                    component="h3"
                    className="font-timesnow"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}

                {paragraph ? (
                  <Typography
                    variant="body"
                    paragraph
                    className="font-gillsans font-light"
                    data-motion-paragraph
                  >
                    {paragraph}
                  </Typography>
                ) : null}
              </div>

              {link ? (
                <div className="-mb-[0.4rem] flex">
                  <Link asChild data-motion-link>
                    <NextLink href={link.url} target={link.target}>
                      <Typography variant="label">{link.title}</Typography>
                    </NextLink>
                  </Link>
                </div>
              ) : null}
            </div>

            <div className="col-span-4 md:col-span-1 lg:col-span-2 lg:col-start-8">
              <MediaParallax className="relative aspect-[182/240] bg-havana">
                {(mediaRef) => (
                  <Media ref={mediaRef} fill fit="cover" {...media_1} />
                )}
              </MediaParallax>
            </div>

            <div className="col-span-4 md:col-span-2 lg:col-span-3">
              <MediaParallax className="relative aspect-[288/390] bg-havana">
                {(mediaRef) => (
                  <Media ref={mediaRef} fill fit="cover" {...media_2} />
                )}
              </MediaParallax>
            </div>
          </Grid>
        </Container>
      )}
    </Motion>
  )
}

/**
 * Asymmetric push B
 */
const AsymmetricPushB = ({
  overline,
  paragraph,
  media_1_media: media_1,
  media_2_media: media_2,
  media_3_media: media_3,
  ...props
}: AsymmetricPushProps) => {
  return (
    <Container {...props}>
      <Grid className="items-center gap-y-gutter">
        <div className="col-span-4 md:col-span-3 lg:col-span-6">
          <MediaParallax className="relative aspect-[605/810] bg-havana">
            {(mediaRef) => (
              <Media ref={mediaRef} fill fit="cover" {...media_1} />
            )}
          </MediaParallax>
        </div>

        <div
          className={cn(
            'col-span-4 md:col-span-2 md:col-start-5 lg:col-span-3 lg:col-start-9',
            'grid gap-y-[3.2rem]'
          )}
        >
          <MediaParallax className="relative aspect-[287/420] bg-havana">
            {(mediaRef) => (
              <Media ref={mediaRef} fill fit="cover" {...media_2} />
            )}
          </MediaParallax>

          <div className="grid grid-cols-4 gap-x-gutter gap-y-gutter lg:grid-cols-3">
            <Motion animation={asymmetricPushRevealAnimation}>
              {(motionRef) => (
                <div
                  ref={motionRef}
                  className={cn(
                    'col-span-4 lg:col-span-3 xl:col-span-2',
                    'grid gap-y-[1.5rem]'
                  )}
                >
                  {overline ? (
                    <Typography
                      variant="label"
                      component="h2"
                      className="font-gillsans uppercase text-copper"
                      data-motion-overline
                    >
                      {overline}
                    </Typography>
                  ) : null}

                  {paragraph ? (
                    <Typography
                      variant="body"
                      paragraph
                      className="font-gillsans font-light"
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}
                </div>
              )}
            </Motion>

            <div
              className={cn('col-span-4 lg:col-span-1', 'md:hidden xl:block')}
            >
              <MediaParallax className="relative aspect-[76/90] bg-havana">
                {(mediaRef) => (
                  <Media ref={mediaRef} fill fit="cover" {...media_3} />
                )}
              </MediaParallax>
            </div>
          </div>
        </div>
      </Grid>
    </Container>
  )
}

/**
 * Asymmetric push C
 */
const AsymmetricPushC = ({
  overline,
  title,
  paragraph,
  link,
  media_1_media: media_1,
  media_2_media: media_2,
  ...props
}: AsymmetricPushProps) => {
  return (
    <Motion animation={asymmetricPushRevealAnimation}>
      {(motionRef) => (
        <Container ref={motionRef} {...props}>
          <Grid className="gap-y-gutter">
            <div className="col-span-4 md:col-span-3 lg:col-span-5">
              <MediaParallax className="relative aspect-[500/600] bg-havana">
                {(mediaRef) => (
                  <Media ref={mediaRef} fill fit="cover" {...media_1} />
                )}
              </MediaParallax>
            </div>

            <div className="col-span-4 md:col-span-3 lg:col-span-4">
              <div
                className={cn(
                  'col-span-4 md:col-span-3 lg:col-span-4',
                  'grid gap-y-[3.2rem] pt-[3.2rem] md:gap-y-[6.4rem] md:pt-[3.2rem] lg:pt-[6.4rem]'
                )}
              >
                <div className="grid gap-y-[0.8rem]">
                  {overline ? (
                    <Typography
                      variant="label"
                      component="h2"
                      className="font-gillsans uppercase text-copper"
                      data-motion-overline
                    >
                      {overline}
                    </Typography>
                  ) : null}

                  {title ? (
                    <Typography
                      variant="h4"
                      component="h3"
                      className="font-timesnow"
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {paragraph ? (
                    <Typography
                      variant="body"
                      paragraph
                      className="font-gillsans font-light"
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}
                </div>

                {link ? (
                  <div className="flex">
                    <Link asChild data-motion-link>
                      <NextLink href={link.url} target={link.target}>
                        <Typography variant="label">{link.title}</Typography>
                      </NextLink>
                    </Link>
                  </div>
                ) : null}
              </div>
            </div>

            <div
              className={cn(
                'col-span-4 items-end md:col-span-1 lg:col-span-2',
                'md:hidden lg:flex'
              )}
            >
              <MediaParallax className="relative aspect-[181/210] w-full bg-havana">
                {(mediaRef) => (
                  <Media ref={mediaRef} fill fit="cover" {...media_2} />
                )}
              </MediaParallax>
            </div>
          </Grid>
        </Container>
      )}
    </Motion>
  )
}

/**
 * Asymmetric push D
 */
const AsymmetricPushD = ({
  media_1_media: media_1,
  media_2_media: media_2,
  media_3_media: media_3,
  ...props
}: AsymmetricPushProps) => {
  return (
    <Container {...props}>
      <Grid className="gap-y-gutter">
        <div className="col-span-4 md:col-span-3 md:mb-[3rem] md:mt-[6rem] lg:col-span-6 lg:mb-[6rem] lg:mt-[12rem]">
          <MediaParallax className="relative aspect-[605/790] bg-havana">
            {(mediaRef) => (
              <Media ref={mediaRef} fill fit="cover" {...media_1} />
            )}
          </MediaParallax>
        </div>

        <div className="col-span-4 md:col-span-2 lg:col-span-3 lg:col-start-8">
          <MediaParallax className="relative aspect-[287/375] bg-havana">
            {(mediaRef) => (
              <Media ref={mediaRef} fill fit="cover" {...media_2} />
            )}
          </MediaParallax>
        </div>

        <div className="col-span-4 flex items-end md:col-span-1 lg:col-span-2">
          <MediaParallax className="relative aspect-[182/277] w-full bg-havana">
            {(mediaRef) => (
              <Media ref={mediaRef} fill fit="cover" {...media_3} />
            )}
          </MediaParallax>
        </div>
      </Grid>
    </Container>
  )
}

/**
 * Asymmetric push
 */
const AsymmetricPush = ({
  variant,
  className,
  ...props
}: AsymmetricPushProps &
  Partial<{
    variant: 'A' | 'B' | 'C' | 'D'
  }>) => {
  return (
    <Section className={className}>
      {variant === 'A' && <AsymmetricPushA {...props} />}
      {variant === 'B' && <AsymmetricPushB {...props} />}
      {variant === 'C' && <AsymmetricPushC {...props} />}
      {variant === 'D' && <AsymmetricPushD {...props} />}
    </Section>
  )
}

export default AsymmetricPush
